import { getHistoryData } from "../../data/ramotswe";
import {
  getCurrentObservatoryName,
  changeTimeSeriesUTCTimeToLocalTime,
  getXLims,
} from "../../utils";
import { DashboardTimeLineGraph } from "../../graphs/base_graphs";
import { DateTime } from "../../imports";
import { observatoryMetadata } from "../../config";
import { d3 } from "../../imports";

export default async function initializeTargetAltitudes() {
  const targetNames = {
    sun: "Sun",
    moon: "Moon",
    ss433: "SS433",
    etacar: "Eta Carina",
    betapic: "Beta Pictoris",
    agpeg: "AG Pegasi",
    tcrb: "T Coronae Borealis",
  };
  const targets = Object.keys(targetNames);

  async function getData() {
    const observatoryName = getCurrentObservatoryName();
    const targetAltitudeData = {};
    const timeDiff = { hours: 24 };
    const now = DateTime.now().setZone("UTC");
    const endDateTime = now.plus(timeDiff);
    const startDateTime = now.minus(timeDiff);

    for (let target of targets) {
      targetAltitudeData[target] = changeTimeSeriesUTCTimeToLocalTime(
        await getHistoryData(
          observatoryName,
          "telemetrytimeframedata",
          startDateTime,
          endDateTime,
          {
            model: "Ephemeris",
            column_names: "target_alt",
            filter_column_names: "target_name",
            filter_values: target,
          }
        )
      );
    }

    const xLims = getXLims(
      targetAltitudeData.sun.map((row) => row.datetime_utc),
      5
    );

    return [targetAltitudeData, xLims];
  }

  // set up the initial data and axes
  const [initialData, xLims] = await getData();
  const targetAltitudeGraph = new DashboardTimeLineGraph(
    "#target-altitudes-graph",
    "Time",
    "Altitude [\u00B0]"
  );

  // set the graph parameters for consistency in initialization & refresh
  function makeLineGraphParams(allGraphData, target, hideSVG, xLims) {
    return [
      allGraphData[target],
      "datetime_utc",
      "target_alt",
      {
        secondAxis: false,
        transition: true,
        staticYlims: [-90, 90],
        extraClasses: [`target-altitude-${target}`],
        hideSVG: hideSVG,
        staticXlims: xLims,
      },
      ,
    ];
  }

  // populate the graphs, saving the names of them
  const targetKeys = {};
  for (let target of targets) {
    targetKeys[target] = targetAltitudeGraph.makeLineGraph(
      ...makeLineGraphParams(initialData, target, true, xLims)
    );

    targetAltitudeGraph.plotElements[targetKeys[target]].attr(
      "targetName",
      target
    );
  }
  targetAltitudeGraph.addNowLine();

  // Adding Legend
  targetAltitudeGraph.svg
    .append("circle")
    .attr("cx", 480)
    .attr("cy", 10)
    .attr("r", 3)
    .style("fill", "yellow");

  targetAltitudeGraph.svg
    .append("text")
    .attr("x", 488)
    .attr("y", 10)
    .text("Sun")
    .style("font-size", "8px")
    .style("fill", "yellow")
    .attr("alignment-baseline", "middle")
    .attr("text-anchor", "left");

  targetAltitudeGraph.svg
    .append("circle")
    .attr("cx", 515)
    .attr("cy", 10)
    .attr("r", 3)
    .style("fill", "ivory");

  targetAltitudeGraph.svg
    .append("text")
    .attr("x", 522)
    .attr("y", 10)
    .text("Moon")
    .style("font-size", "8px")
    .attr("alignment-baseline", "middle")
    .style("fill", "ivory")
    .attr("text-anchor", "left");

  // Adding Tooltip
  const Tooltip = d3
    .select("body")
    .append("div")
    .style("opacity", 0)
    .attr("class", "tooltipTarget")
    .style("position", "absolute")
    .style("left", 0)
    .style("top", 0)
    .style("background-color", "#333333")
    .style("border", "solid")
    .style("border-width", "0px")
    .style("border-radius", "2px")
    .style("padding", "10px")
    .style("font-size", "1.0rem")
    .style("color", "#ffffff");

  const mouseover = function (event, d) {
    Tooltip.style("opacity", 1);
  };
  const mousemove = function (event, d) {
    Tooltip.html(targetNames[event.target.getAttribute("targetName")])
      .style("left", `${event.clientX}px`)
      .style("top", `${event.clientY - 60}px`);
  };
  const mouseleave = function (event, d) {
    Tooltip.style("opacity", 0);
  };

  // Adding Events
  for (let target of targets) {
    targetAltitudeGraph.plotElements[targetKeys[target]]
      .on("mouseover", mouseover)
      .on("mousemove", mousemove)
      .on("mouseleave", mouseleave);
  }

  targetAltitudeGraph.showLoading();

  // define the refresh function
  async function refresh() {
    targetAltitudeGraph.showLoading();
    const [newData, xLims] = await getData();

    // updateData has the same format as make*Graph, but with the key as param 1
    for (let target of targets) {
      targetAltitudeGraph.updateData(
        targetKeys[target],
        ...makeLineGraphParams(newData, target, false, xLims)
      );
    }

    targetAltitudeGraph.refreshNowLine();
  }

  return refresh;
}
